import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Popover } from 'radix-ui';
import { Bell, ClipboardList, FileWarning, MessageSquare, PackageCheck, PackageX, ReceiptText, type LucideIcon } from 'lucide-react';
import { useAgency } from '@/auth/AgencyProvider';
import { useNotifications } from '@/data/useNotifications';
import type { Task } from '@/data/types';
import { badgeText } from '@/lib/chat';
import { dateTimeInZone } from '@/lib/dates';
import { notificationHref, type Notification, type NotificationKind } from '@/lib/notifications';
import { taskTitle } from '@/lib/taskTitle';
import { cn } from '@/lib/utils';

/**
 * The bell (change spec 2026-09-24/topbar, design 2.5). The badge is the feed's unread count for the
 * current agency. Opening the panel marks everything up to the newest notice read (the badge clears at
 * once); the notices that were unread keep their dot while the panel stays open, so the office still
 * sees what was new. Each notice is a link to where the office acts on it.
 */

const KIND_ICON: Record<NotificationKind, LucideIcon> = {
  chat_message: MessageSquare,
  load_rejected: PackageX,
  task_assigned: ClipboardList,
  document_expiring: FileWarning,
  invoice_overdue: ReceiptText,
  load_delivered: PackageCheck,
  pod_received: PackageCheck,
};

const TASK_SOURCE: Partial<Record<NotificationKind, Task['source']>> = {
  load_rejected: 'load_rejected',
  document_expiring: 'document_expiry',
  invoice_overdue: 'invoice_overdue',
  task_assigned: 'manual',
};

type T = ReturnType<typeof useTranslation>['t'];

/** The notice's line, in the UI language. Task notices reuse the Upcoming Tasks copy (taskTitle). */
export function notificationText(n: Notification, t: T): string {
  switch (n.kind) {
    case 'chat_message':
      return t('notifications.chat', { driver: n.driverName ?? t('notifications.aDriver'), count: Number(n.detail ?? 1) });
    case 'load_delivered':
      return t('notifications.delivered', { load: n.loadNumber ?? '' });
    case 'pod_received':
      return t('notifications.pod', { load: n.loadNumber ?? '' });
    default: {
      const source = TASK_SOURCE[n.kind] ?? 'manual';
      return taskTitle({ source, title: n.title ?? '', detail: n.detail, loadId: n.loadId }, n.loadNumber, t);
    }
  }
}

export function NotificationsBell() {
  const { t, i18n } = useTranslation();
  const agency = useAgency();
  const timeZone = agency.status === 'ready' ? agency.agency.timeZone : 'America/Chicago';
  const feed = useNotifications();
  const [open, setOpen] = React.useState(false);
  // The notices that were unread when the panel opened: they keep their dot until it closes.
  const [wasUnread, setWasUnread] = React.useState<Set<string>>(new Set());
  const badge = badgeText(feed.unreadCount);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) return;
    setWasUnread(new Set(feed.items.filter((n) => n.unread).map((n) => n.id)));
    const newest = feed.items[0];
    if (newest && feed.unreadCount > 0) void feed.markRead(newest.at);
  };

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={feed.unreadCount > 0 ? t('topbar.notificationCount', { count: feed.unreadCount }) : t('topbar.notifications')}
          className="relative grid size-touch shrink-0 place-items-center rounded-md text-ink-secondary hover:bg-secondary"
        >
          <Bell className="size-5" aria-hidden="true" />
          {badge && (
            <span className="tabular-figures absolute top-1 right-1 grid min-w-5 place-items-center rounded-full bg-destructive px-1 text-xs font-semibold text-destructive-foreground">
              {badge}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={16}
          aria-label={t('notifications.title')}
          className="z-50 flex max-h-[min(32rem,calc(100dvh-6rem))] w-[min(24rem,calc(100vw-2rem))] flex-col rounded-lg border border-hairline bg-popover text-popover-foreground shadow-overlay"
        >
          <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
            <h2 className="text-base font-semibold text-ink">{t('notifications.title')}</h2>
          </div>
          <div className="relative min-h-0 flex-1 overflow-y-auto">
            {feed.status === 'error' && feed.items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('notifications.error')}</p>
            ) : feed.status === 'loading' && feed.items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('common.loading')}</p>
            ) : feed.items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('notifications.empty')}</p>
            ) : (
              <ul className="divide-y divide-hairline">
                {feed.items.map((n) => {
                  const Icon = KIND_ICON[n.kind];
                  const unread = n.unread || wasUnread.has(n.id);
                  return (
                    <li key={n.id}>
                      <Link
                        to={notificationHref(n)}
                        onClick={() => setOpen(false)}
                        className={cn('flex gap-3 px-4 py-3 hover:bg-secondary', unread && 'bg-accent-wash')}
                      >
                        <Icon className="mt-0.5 size-5 shrink-0 text-ink-muted" aria-hidden="true" />
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className={cn('text-sm text-ink', unread && 'font-semibold')}>{notificationText(n, t)}</span>
                          <span className="text-xs text-ink-muted">
                            {t(`notifications.kinds.${n.kind}`)} · {dateTimeInZone(n.at, timeZone, i18n.resolvedLanguage ?? 'en')}
                          </span>
                        </span>
                        {unread && (
                          <span
                            data-testid="notification-unread"
                            role="img"
                            className="mt-1.5 size-2 shrink-0 rounded-full bg-accent-base"
                            aria-label={t('notifications.unread')}
                          />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
